/* eslint-disable jsx-a11y/label-has-associated-control */
import { FieldConfig, useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { GroupBase, InputActionMeta, OptionsOrGroups } from 'react-select';
import { toast } from 'react-toastify';
import SearchSelect, { AsyncSearchSelect } from './SearchSelect';

interface IProps extends FieldConfig {
  label: string;
  placeholder: string;
  onBlur?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: any) => void;
  onFocus?: () => void;
  showBlue?: boolean;
  showGreen?: boolean;
  stepName?: string;
  updateCallback?: (...args: any) => Promise<any> | any;
  refreshCallback?: (...args: any) => Promise<any> | any;
  getOptionLabel?: (...args: any) => Promise<any> | any;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  loadOptions?: (inputValue: string, callback: any) => void;
  defaultOptioins?: boolean | any;
  children?: any;
}

export function FormikAsyncSearchSelect<T>({
  label,
  placeholder,
  showBlue,
  showGreen,
  stepName,
  updateCallback,
  refreshCallback,
  getOptionLabel,
  loadOptions,
  onFocus,
  onInput,
  onInputChange,
  defaultOptioins,
  ...props
}: IProps) {
  const [field, meta, helpers] = useField(props);
  const { handleBlur } = useFormikContext<T>();
  const { setValue, setTouched } = helpers;
  const [colorBlueState, setColorBlueState] = useState({});
  const [colorGreenState, setColorGreenState] = useState({});
  const [colorRedState, setColorRedState] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBlured, setIsBlured] = useState(true);

  const setFieldProps = (selectedOption: any) => {
    setValue(selectedOption.value);
  };

  const defaultValue = (choices: any, value: any) =>
    choices ? choices.find((option: any) => option.value === value) : '';

  const { initialValue, value, error } = meta;
  const { name } = field;

  useEffect(() => {
    if (value !== initialValue) {
      setColorBlueState({ [name]: name });
    } else {
      setColorBlueState({});
    }
    return () => {
      setColorGreenState({});
    };
  }, [value]);

  const processOnBlur = async () => {
    setColorRedState({});
    let updateObject;
    if (!error) {
      const oldVal: string = initialValue;
      let newVal: string | null = value;
      if (newVal !== oldVal) {
        if (newVal === '') {
          newVal = null;
        }
        updateObject = { [name]: newVal };
        try {
          setIsUpdating(true);
          if (updateCallback) await updateCallback(updateObject);
          setIsUpdating(false);
          setColorGreenState({ [name]: true });
          if (refreshCallback) await refreshCallback(updateObject);
          setColorBlueState({});
          setTimeout(() => {
            setColorGreenState({});
          }, 2000);
        } catch (err: any) {
          setColorRedState({ [name]: true });
          toast.error('An error occured', err);
        }
      }
    }
  };

  const showError = isBlured && meta.touched && meta.error;

  return (
    <div className="relative" style={{ borderColor: 'blue' }}>
      <div className="text-xs h-4 px-2 text-red-00 ">
        {showError ? meta.error : ''}
      </div>
      <AsyncSearchSelect
        loadOptions={loadOptions}
        defaultOptions={defaultOptioins}
        id={label}
        onChange={(selectedOption: any) => {
          setFieldProps(selectedOption);
          if (onInput) onInput(selectedOption);
        }}
        label={label}
        placeholder={placeholder}
        onBlur={(e: any) => {
          handleBlur(e);
          setIsBlured(true);
        }}
        onFocus={() => {
          if (onFocus) onFocus();
          setIsBlured(false);
        }}
        onMenuOpen={() => setTouched(true)}
        showBlue={name in colorBlueState}
        showGreen={name in colorGreenState}
        showRed={name in colorRedState}
        isUpdating={isUpdating}
      />
    </div>
  );
}

FormikAsyncSearchSelect.defaultProps = {
  onFocus: () => ({}),
  onBlur: () => ({}),
  getOptionLabel: () => ({}),
  showBlue: false,
  showGreen: false,
  stepName: '',
  refreshCallback: () => ({}),
  updateCallback: () => ({}),
  onInputChange: () => ({}),
  onInput: () => ({}),
  loadOptions: () => ({}),
  children: <></>,
};

export default FormikAsyncSearchSelect;
